import { authService } from 'auth/services';

import {
  buildStringFromParts,
  formatDate,
  getICSFile,
  isMaskedInformation,
  mapPhoneNumber,
  parseTimeSlot,
} from 'features/order-tracker/utils';
import {
  APPOINTMENT_STATUSES,
  APPOINTMENT_TYPES,
  MILESTONE_STATUSES,
  ORDER_SERVICES_TYPES,
} from 'features/order-tracker/utils/entries';

import {
  APPOINTMENT_COMPLETED_STATUSES,
  APPOINTMENT_TEXT,
  KCI2_ASSURE_APPT_TYPE,
  MILESTONES_NAMES_MAPPER,
  MS_IN_DAY,
  OPENREACH_ENGINEER_APPOINTMENT_TITLE,
  PRODUCT_TYPES,
  TITLE_NAMES_MAPPER,
} from './constants';

export const getAppointmentContactData = (siteContact: dto.SiteContact): string => {
  const { Title, FirstName, LastName, Mobile, WorkPhone, HomePhone } = siteContact;
  const fullName = buildStringFromParts([Title, FirstName, LastName], ' ');
  const phone = Mobile || HomePhone || WorkPhone;
  const formattedPhone = isMaskedInformation(phone) ? phone : mapPhoneNumber(phone);

  return phone ? `${fullName}: ${formattedPhone}` : fullName;
};

export const getSiteContactDetails = (siteContact: dto.SiteContact): common.SiteContact => {
  const { Title, FirstName, LastName, Mobile, WorkPhone, HomePhone } = siteContact;
  const fullName = buildStringFromParts([Title, FirstName, LastName], ' ');
  const phone = Mobile || HomePhone || WorkPhone;
  return {
    userName: fullName,
    userPhone: phone,
  };
};

const getAppointmentType = (productType: number): common.AppointmentType => {
  switch (productType) {
    case PRODUCT_TYPES.expert:
      return APPOINTMENT_TYPES.expert;
    case PRODUCT_TYPES.broadband:
      return APPOINTMENT_TYPES.broadband;
    default:
      return APPOINTMENT_TYPES.engineer;
  }
};

const getAppointmentSlot = (slot: string): string => {
  const [slotStart, slotEnd] = parseTimeSlot(slot);

  return `${slotStart} and ${slotEnd}`;
};

const getAppointmentDateWithSlot = (date: string, slot: string): string =>
  `${formatDate('dddd D MMMM YYYY', date)}, starting between ${slot}`;

const getAppointmentDetailsText = (appointmentType: common.AppointmentType, productName?: string): string => {
  switch (appointmentType) {
    case APPOINTMENT_TYPES.expert:
      return APPOINTMENT_TEXT.expert;
    case APPOINTMENT_TYPES.engineer:
      return `${APPOINTMENT_TEXT.engineer} ${productName}`;
    default:
      return APPOINTMENT_TEXT.broadband;
  }
};

const getReminderUrl = (appointment: dto.Product, appointmentType: common.AppointmentType): string => {
  const { ProductName, EngineeringAppointmentDate, EngineeringAppointmentSlot } = appointment;

  const details = getAppointmentDetailsText(appointmentType, ProductName);
  const [slotStartDate, slotEndDate] = parseTimeSlot(EngineeringAppointmentSlot).map((time: string) =>
    new Date(new Date(EngineeringAppointmentDate).setHours(parseInt(time, 10))),
  );

  return URL.createObjectURL(getICSFile(details, slotStartDate, slotEndDate));
};

const getAppointmentTitle = (appointmentType: common.AppointmentType): string =>
  appointmentType === APPOINTMENT_TYPES.expert ? appointmentType : OPENREACH_ENGINEER_APPOINTMENT_TITLE;

const formatEngineerAppointmentMilestone = (
  {
    AppointmentStatus,
    EngineeringAppointmentDate,
    OrderItemReference,
    EngineeringAppointmentSlot,
    FTTPInstallProcess = '',
    ProductCategory,
  }: dto.Product,
  appointmentType: common.AppointmentType,
  installationAddress: string,
  isCompleted: boolean,
): common.OrderMilestone => {
  const formattedDateWithSlot = EngineeringAppointmentSlot
    ? getAppointmentDateWithSlot(EngineeringAppointmentDate, getAppointmentSlot(EngineeringAppointmentSlot))
    : '';

  return {
    id: OrderItemReference,
    formattedDate: formatDate('dddd D MMMM YYYY', EngineeringAppointmentDate),
    formattedDateWithSlot,
    milestoneStatus: isCompleted ? MILESTONE_STATUSES.complete : MILESTONE_STATUSES.incomplete,
    status: AppointmentStatus,
    title: getAppointmentTitle(appointmentType),
    type: ORDER_SERVICES_TYPES.Appointment,
    subTitle: installationAddress,
    fttpInstallProcess: FTTPInstallProcess,
    productCategory: ProductCategory,
    displayTitle: MILESTONES_NAMES_MAPPER[getAppointmentTitle(appointmentType)] || getAppointmentTitle(appointmentType),
  };
};

const formatEngineerAppointment = (
  appointment: dto.Product,
  appointmentType: common.AppointmentType,
  isKci2AssureOrder: boolean,
): common.EngineerAppointment => {
  const {
    AppointmentStatus,
    EngineeringAppointmentDate,
    EngineeringAppointmentSlot,
    OrderItemReference,
    SiteContact,
    OrderKey,
    InstallationAddress,
    FTTPInstallProcess = '',
  } = appointment;

  const [dateWithoutTime] = EngineeringAppointmentDate.split('T');
  const isDateLessThan24Hours = Date.parse(`${dateWithoutTime}T00:00:00`) - MS_IN_DAY / 2 <= Date.now();
  const isAppointmentToday = Date.parse(`${dateWithoutTime}T00:00:00`) === new Date().setHours(0, 0, 0, 0);
  const isFutureDate = Date.parse(`${dateWithoutTime}T00:00:00`) >= new Date().setHours(0, 0, 0, 0);
  const appointmentTimeSlot = getAppointmentSlot(EngineeringAppointmentSlot);

  return {
    formattedDate: getAppointmentDateWithSlot(EngineeringAppointmentDate, appointmentTimeSlot),
    engineeringAppointmentDate: EngineeringAppointmentDate,
    isKci2AssureOrder,
    orderItemReference: OrderItemReference,
    reminderUrl: getReminderUrl(appointment, appointmentType),
    status: AppointmentStatus,
    siteContact: SiteContact ? getAppointmentContactData(SiteContact) : '',
    siteDetails: SiteContact ? getSiteContactDetails(SiteContact) : { userName: '', userPhone: '' },
    title: getAppointmentTitle(appointmentType),
    displayTitle: TITLE_NAMES_MAPPER[getAppointmentTitle(appointmentType)] || getAppointmentTitle(appointmentType),
    type: appointmentType,
    installationAddress: InstallationAddress,
    fttpInstallProcess: FTTPInstallProcess,
    email: SiteContact?.Email,
    orderKey: OrderKey,
    isDateLessThan24Hours,
    isAppointmentToday,
    timeSlot: appointmentTimeSlot,
    isFutureDate,
  };
};

export const getAppointmentInfo = (appointment: transformedResponse.Appointment): common.AppointmentInfo => {
  const isAuthenticated = authService.isAuthenticated();
  const { AppointmentStatus, InstallationAddress, FTTPInstallProcess, ProductType } = appointment;
  const updatedInstallationAddress = isAuthenticated ? InstallationAddress : `Postcode: ${InstallationAddress}`;
  const appointmentType = getAppointmentType(ProductType);
  const isKci2AssureOrder =
    appointmentType === APPOINTMENT_TYPES.broadband &&
    FTTPInstallProcess === KCI2_ASSURE_APPT_TYPE &&
    AppointmentStatus === APPOINTMENT_STATUSES.Booked;
  const isCompleted = APPOINTMENT_COMPLETED_STATUSES.includes(AppointmentStatus);
  let formattedEngineerAppointmentMilestone;

  const formattedEngineerAppointment = formatEngineerAppointment(appointment, appointmentType, isKci2AssureOrder);

  if (!isKci2AssureOrder) {
    formattedEngineerAppointmentMilestone = formatEngineerAppointmentMilestone(
      appointment,
      appointmentType,
      updatedInstallationAddress,
      isCompleted,
    );
  }

  return {
    address: updatedInstallationAddress,
    engineerAppointment: formattedEngineerAppointment,
    engineerAppointmentMilestone: formattedEngineerAppointmentMilestone,
    isCompleted,
  };
};
